import type {
  ConfirmNonApiSpendPreviewResponse,
  NonApiSpendListItem,
  NonApiSpendListParams,
  NonApiSpendPreviewListItem,
  NonApiSpendPreviewListParams,
  UploadNonApiSpendPreviewParams,
  UploadNonApiSpendPreviewResponse,
} from './types/non-api-spend'

import type { PagingRequest } from '#/api/type'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'
import { DateRanges } from '#/utils'

export const nonApiSpendApi = defineApi('operation', ({ prefix }) => {
  function getNonApiSpendListParams(params: NonApiSpendListParams) {
    return {
      condition: {
        dateRange: DateRanges.datesOrNull(params.condition.dateRange),
        updatedDateRange: DateRanges.datesOrNull(
          params.condition.updatedDateRange,
        ),
        mediaPlatform: params.condition.mediaPlatform,
        accountId: params.condition.accountId,
        accountName: params.condition.accountName,
        dataSource: params.condition.dataSource,
      },
      sortBy: params.sortBy,
      orderBy: params.orderBy,
    }
  }

  function getNonApiSpendList(params: PagingRequest<NonApiSpendListParams>) {
    return requestClient
      .post(prefix('adsSpendSupplement/query'), {
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        sortBy: params.sortBy,
        orderBy: params.orderBy,
        condition: params.condition,
      })
      .then((res) => {
        return {
          items: (res?.list || []).map((item: NonApiSpendListItem) => ({
            id: item.id,
            reportDate: item.reportDate,
            mediaPlatform: item.mediaPlatform,
            accountId: item.accountId,
            accountName: item.accountName,
            currency: item.currency,
            spend: item.spend,
            dataSource: item.dataSource,
            status: item.status,
            updatedBy: item.updatedBy,
            updatedAt: item.updatedAt,
          })),
          total: res?.total || 0,
        }
      })
  }

  function updateNonApiSpend(params: { id: number; spend: number }) {
    return requestClient.post(prefix('adsSpendSupplement/update'), {
      id: params.id,
      spend: params.spend,
    })
  }

  function uploadNonApiSpendPreview(params: UploadNonApiSpendPreviewParams) {
    const formData = new FormData()
    formData.append('bizCode', params.bizCode)
    formData.append('mediaPlatform', params.mediaPlatform)
    formData.append('file', params.file)

    return requestClient
      .post<UploadNonApiSpendPreviewResponse>(
        prefix('dataUpload/syncPreview'),
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      )
      .then((res) => ({
        totalCount: res?.totalCount,
        successCount: res?.successCount,
        failCount: res?.failCount,
        uploadLogId: res?.uploadLogId,
      }))
  }

  function queryNonApiSpendPreviewList(params: NonApiSpendPreviewListParams) {
    return requestClient
      .post(prefix('dataUpload/queryPreviewList'), {
        uploadLogId: params.uploadLogId,
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
      })
      .then((res) => ({
        list: (res?.list || []).map((item: NonApiSpendPreviewListItem) => ({
          id: item.id,
          rowIndex: item.rowIndex,
          currency: item.currency,
          spend: item.spend,
          mediaPlatform: item.mediaPlatform,
          accountId: item.accountId,
          accountName: item.accountName,
          reportDate: item.reportDate,
          status: item.status,
          invalidReason: item.invalidReason,
          updatedBy: item.updatedBy,
          createdAt: item.createdAt,
        })),
        total: res?.total,
      }))
  }

  function confirmNonApiSpendPreview(uploadLogId: number) {
    return requestClient
      .post<ConfirmNonApiSpendPreviewResponse>(
        prefix('dataUpload/confirmPreviewList'),
        {
          uploadLogId,
        },
      )
      .then((res) => ({
        successCount: res?.successCount,
        totalCount: res?.totalCount,
      }))
  }

  function downloadTemplate(bizCode: string) {
    return requestClient
      .post(prefix('dataUpload/downloadTemplate'), {
        bizCode,
      })
      .then((res) => res?.url)
  }

  return {
    getNonApiSpendList,
    getNonApiSpendListParams,
    updateNonApiSpend,
    uploadNonApiSpendPreview,
    queryNonApiSpendPreviewList,
    confirmNonApiSpendPreview,
    downloadTemplate,
  }
})
